/**
 * New-window receipt printer.
 *
 * Android Chrome refuses to print the cashier SPA's OWN document — even
 * Save-as-PDF comes out blank — because the app locks the viewport
 * (`html, body { height:100%; overflow:hidden }` in pos.css) and Android's print
 * pipeline clips the whole page away. Every in-page technique (hidden iframe,
 * `@media print` overlay, normal-flow container, root overflow resets) printed
 * blank on the customer's Android tablet while working on desktop.
 *
 * The approach that works — the same one `reports/reportPrint.ts` already uses,
 * and how Odoo prints on this tablet — is to open a SEPARATE top-level window
 * containing ONLY the receipt document (its own CSS, none of the app's) and
 * print that.
 *
 * Hard-won details:
 *  - On Android do NOT close the window on `afterprint`: there the event fires
 *    mid-render and aborts the job ("There was a problem printing the page").
 *    The previous receipt window is closed lazily on the NEXT sale instead.
 *    Everywhere else `afterprint` means the dialog is gone and the job is
 *    spooled, so the window closes itself and the cashier lands back on the
 *    till — a tab left open per sale had them hunting through tabs at the
 *    counter, and a print dialog opening in a tab they had already left
 *    froze the app behind it (`window.print()` is synchronous).
 *  - A small settle delay before `print()` lets fonts/layout finish.
 *  - The window carries a Print/Close toolbar (hidden when printing) so the
 *    cashier can always re-print or dismiss by hand — a direct gesture that no
 *    pop-up blocker or print quirk can defeat.
 *
 * Pop-up note: `window.open` from the async post-charge callback can be blocked;
 * `printReceipt` throws `POPUP_BLOCKED` so the store can hint the cashier to
 * allow pop-ups or tap "Print again" (a direct gesture, never blocked).
 */

// Cap on waiting for the receipt window's fonts + logo image before printing.
const WINDOW_READY_MAX_MS = 1500;
// Small settle delay after resources are ready, before firing print().
const SETTLE_MS = 300;
// Only close a PREVIOUS receipt window if it's older than this — so rapid
// multi-copy prints (which reuse this path back-to-back) never close a window
// that is still rendering. A genuinely separate sale is always older than this.
const PREV_WINDOW_KEEP_MS = 8000;
// Grace after `afterprint` before the window closes itself, so a browser that
// fires the event a beat before the job is fully handed off is not cut short.
const CLOSE_AFTER_PRINT_MS = 400;

/**
 * Android Chrome fires `afterprint` while the page is still being rendered for
 * the print service; closing on it there kills the job (see the header note).
 * The window is then left open and closed lazily on the next print instead.
 */
function closesOnAfterprint(): boolean {
  return !/android/i.test(typeof navigator === 'undefined' ? '' : navigator.userAgent);
}

/** Thrown when the browser blocks the print window (pop-up blocker). */
export const POPUP_BLOCKED = 'POPUP_BLOCKED';

// The previous sale's print window, closed lazily on the next print so receipt
// tabs don't pile up — without ever closing a window mid-render.
let lastPrintWin: Window | null = null;
let lastPrintAt = 0;

/**
 * Resolve once every <img> in `doc` has loaded (or errored — a broken logo must
 * not block the receipt). Resolves immediately when there are none.
 */
function imagesReady(doc: Document): Promise<void> {
  const imgs = Array.from(doc.images);
  if (imgs.length === 0) return Promise.resolve();
  return Promise.all(
    imgs.map((img) =>
      img.complete
        ? Promise.resolve()
        : new Promise<void>((resolve) => {
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          }),
    ),
  ).then(() => undefined);
}

/**
 * Add a floating Print/Close toolbar to the print window. Hidden by `@media
 * print` so it never appears on paper. Gives the cashier a reliable manual
 * trigger if auto-print is refused or dismissed.
 *
 * `hint` draws one line of text beside the buttons. It is used when nothing
 * will fire by itself (see `autoPrint`), so the window does not look like a
 * print that failed.
 */
function injectToolbar(win: Window, hint?: string): void {
  const doc = win.document;
  const style = doc.createElement('style');
  style.textContent = `
    .matix-print-bar {
      position: fixed; top: 8px; inset-inline-end: 8px; display: flex; gap: 8px;
      align-items: center; z-index: 2147483647; font-family: system-ui, sans-serif;
    }
    .matix-print-bar button {
      padding: 10px 16px; font-size: 14px; border: none; border-radius: 6px;
      cursor: pointer;
    }
    .matix-print-bar .p { background: #2563eb; color: #fff; }
    .matix-print-bar .c { background: #e5e7eb; color: #111; }
    .matix-print-bar .h {
      background: #fef3c7; color: #78350f; padding: 8px 12px; border-radius: 6px;
      font-size: 13px; max-width: 46ch; line-height: 1.4;
    }
    @media print { .matix-print-bar { display: none !important; } }
  `;
  doc.head.appendChild(style);

  const bar = doc.createElement('div');
  bar.className = 'matix-print-bar';
  bar.innerHTML =
    '<button class="p" type="button">Print · طباعة</button>' +
    '<button class="c" type="button">Close · إغلاق</button>';
  if (hint) {
    const h = doc.createElement('span');
    h.className = 'h';
    // textContent, not innerHTML: the hint is text, and this document is
    // written into a window the cashier is looking at.
    h.textContent = hint;
    bar.insertBefore(h, bar.firstChild);
  }
  doc.body.appendChild(bar);
  bar.querySelector('.p')?.addEventListener('click', () => {
    try { win.print(); } catch { /* ignore */ }
  });
  bar.querySelector('.c')?.addEventListener('click', () => {
    try { win.close(); } catch { /* ignore */ }
  });
}

export interface PrintReceiptOptions {
  /**
   * Fire `win.print()` ourselves once the window is ready. Default TRUE —
   * every browser caller is unchanged.
   *
   * ⚠ PASS FALSE WHERE AN AUTOMATIC PRINT CANNOT WORK, and the phone shell is
   * the case it was added for. `window.print()` is SYNCHRONOUS for the
   * renderer: a webview that has no print pipeline to drive still takes the
   * call, and the windows of one webview can share a renderer — so the cashier
   * behind the slip stops responding, nothing comes out, and the window does
   * not close either, because `closesOnAfterprint()` excludes Android on
   * purpose. That is the «لا بيطبع ولا بيغلق» report.
   *
   * With `autoPrint: false` the receipt is still opened and still complete —
   * it must never be a silent loss — but nothing fires by itself. The toolbar's
   * Print button stays, and a tap on it is a DIRECT GESTURE, which is the one
   * form of print that has always been reliable here.
   */
  autoPrint?: boolean;
  /** One line of text drawn beside the toolbar buttons. */
  hint?: string;
}

/**
 * Print a fully-formed HTML receipt document to the user's default printer.
 *
 * @param html — a complete `<!DOCTYPE html>` document (from `buildReceiptHtml`).
 * @param opts — see `PrintReceiptOptions`; the defaults are today's behaviour.
 *
 * Opens the receipt in a new top-level window, waits for its fonts + logo, and
 * prints it. The window is left open (a Print/Close toolbar lets the cashier
 * re-print by hand) and closed on the next print. Rejects with `POPUP_BLOCKED`
 * if the browser blocked the window.
 */
export async function printReceipt(html: string, opts: PrintReceiptOptions = {}): Promise<void> {
  const { autoPrint = true, hint } = opts;
  if (typeof window === 'undefined') {
    throw new Error('printReceipt: no window — must be called in the browser');
  }

  // Lazily close the PREVIOUS sale's window (its print finished long ago) so
  // tabs don't accumulate — but never a window younger than PREV_WINDOW_KEEP_MS
  // (a rapid multi-copy print might still be rendering it).
  const now = Date.now();
  if (lastPrintWin && !lastPrintWin.closed && now - lastPrintAt > PREV_WINDOW_KEEP_MS) {
    try { lastPrintWin.close(); } catch { /* ignore */ }
    lastPrintWin = null;
  }

  const win = window.open('', '_blank');
  if (!win) {
    throw new Error(POPUP_BLOCKED);
  }
  lastPrintWin = win;
  lastPrintAt = now;

  win.document.open();
  win.document.write(html);
  win.document.close();
  injectToolbar(win, hint);

  if (closesOnAfterprint()) {
    win.addEventListener('afterprint', () => {
      win.setTimeout(() => {
        try { win.close(); } catch { /* already gone */ }
      }, CLOSE_AFTER_PRINT_MS);
    }, { once: true });
  }

  // ⚠ NOTHING ELSE TO DO WHEN THE PRINT IS NOT OURS TO FIRE.
  //
  // Returning here skips the resource wait AND the `print()` call. The wait
  // exists so the paper is not snapshotted mid-paint; with no snapshot to take
  // it only delays handing the window over. Leaving before the blocking call
  // is the whole point — see PrintReceiptOptions.autoPrint.
  if (!autoPrint) {
    try { win.focus(); } catch { /* a webview may refuse focus; harmless */ }
    return;
  }

  // Wait for the receipt's fonts (Arabic glyphs) and the base64 logo image so
  // the print doesn't snapshot before they paint — capped so a stuck resource
  // (or a browser without `fonts.ready`) can never hang the print.
  const fontsReady: Promise<void> = win.document.fonts?.ready
    ? win.document.fonts.ready.then(() => undefined).catch(() => undefined)
    : Promise.resolve();
  const ready = Promise.all([fontsReady, imagesReady(win.document)]).then(() => undefined);
  const cap = new Promise<void>((resolve) => window.setTimeout(resolve, WINDOW_READY_MAX_MS));
  await Promise.race([ready, cap]);
  // Let layout/paint settle, then auto-fire the print dialog. The window closes
  // itself on `afterprint` except on Android — closing mid-render is what
  // caused "There was a problem printing the page" there.
  await new Promise<void>((resolve) => window.setTimeout(resolve, SETTLE_MS));
  try {
    win.focus();
    win.print();
  } catch {
    // Auto-print refused — the toolbar's Print button remains for the cashier.
  }
}
